/**
 * PerformanceView — Main container for the KPI Performance module.
 *
 * Navigation via PerformanceSidebar (T2). Content switches on uiStore.performanceSection.
 * Slide-out detail panel for individual KPIs.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Plus from 'lucide-react/dist/esm/icons/plus';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down';
import { useKPIStore } from '../../../stores/kpiStore';
import { useUIStore } from '../../../stores/uiStore';
import { useOrgStore } from '../../../stores/orgStore';
import DashboardMyKPIs from './DashboardMyKPIs';
import DashboardTeam from './DashboardTeam';
import DashboardOrg from './DashboardOrg';
import TemplateSelector from './TemplateSelector';
import KPIDetailPanel from './KPIDetailPanel';
import CycleManager from './CycleManager';
import AssignKPIModal from './AssignKPIModal';
import type { KPIDefinition } from '../../../services/api/kpi';

const PerformanceView: React.FC = () => {
  const {
    cycles, activeCycleId, setActiveCycleId, fetchCycles,
    selectedAssignmentId, setSelectedAssignment,
    showAssignModal, setShowAssignModal,
    setShowCheckInForm,
    myDashboard,
  } = useKPIStore();
  const { performanceSection, setPerformanceSection } = useUIStore();

  const { userContext } = useOrgStore();
  const isAdmin = userContext?.system_role !== 'user';

  // Selected template for assignment flow
  const [selectedTemplate, setSelectedTemplate] = useState<KPIDefinition | null>(null);

  useEffect(() => {
    fetchCycles();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { teamDashboard, orgDashboard } = useKPIStore();
  const selectedAssignment = useMemo(() => {
    if (!selectedAssignmentId) return null;
    const fromMy = myDashboard?.assignments.find(a => a.id === selectedAssignmentId);
    if (fromMy) return fromMy;
    if (teamDashboard?.members) {
      for (const member of teamDashboard.members) {
        const fromTeam = member.assignments.find(a => a.id === selectedAssignmentId);
        if (fromTeam) return fromTeam;
      }
    }
    if (orgDashboard?.members) {
      for (const member of orgDashboard.members) {
        const fromOrg = member.assignments.find(a => a.id === selectedAssignmentId);
        if (fromOrg) return fromOrg;
      }
    }
    return null;
  }, [selectedAssignmentId, myDashboard, teamDashboard, orgDashboard]);

  const handleSelectAssignment = useCallback((id: string) => {
    setSelectedAssignment(id);
  }, [setSelectedAssignment]);

  const handleCheckIn = useCallback((id: string) => {
    setSelectedAssignment(id);
    setShowCheckInForm(true);
  }, [setSelectedAssignment, setShowCheckInForm]);

  const handleSelectTemplate = useCallback((template: KPIDefinition) => {
    setSelectedTemplate(template);
    setShowAssignModal(true);
  }, [setShowAssignModal]);

  const handleCreateCustom = useCallback(() => {
    setSelectedTemplate(null);
    setShowAssignModal(true);
  }, [setShowAssignModal]);

  // Active cycle for display
  const activeCycle = cycles.find(c => c.id === activeCycleId);

  return (
    <div className="flex-1 flex flex-col animate-fade-in min-h-0">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-subtle">
        <div>
          <h1 className="text-lg font-bold text-content">Performance</h1>
          <p className="text-xs text-content-muted mt-0.5">
            {activeCycle ? activeCycle.name : 'KPI tracking and insights'}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {cycles.length > 0 && (
            <div className="relative">
              <select
                value={activeCycleId || ''}
                onChange={(e) => setActiveCycleId(e.target.value || null)}
                className="pl-3 pr-8 py-2 rounded-lg bg-surface-primary border border-subtle text-content-secondary text-xs outline-none cursor-pointer appearance-none focus:border-brand/40 transition-all"
              >
                {cycles.map(c => (
                  <option key={c.id} value={c.id} className="bg-surface-page">
                    {c.name} ({c.status})
                  </option>
                ))}
              </select>
              <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-content-muted pointer-events-none" />
            </div>
          )}
          {isAdmin && (
            <button
              onClick={handleCreateCustom}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-status-success/10 text-status-success text-xs font-medium hover:bg-status-success/20 transition-colors border border-status-success/20 cursor-pointer"
            >
              <Plus size={14} />
              Assign KPI
            </button>
          )}
        </div>
      </div>

      {/* Content — switched by performanceSection from sidebar */}
      <div className="flex-1 overflow-y-auto p-6 min-h-0">
        {performanceSection === 'my-kpis' && (
          <DashboardMyKPIs
            onSelectAssignment={handleSelectAssignment}
            onCheckIn={handleCheckIn}
            onNavigateToCycles={isAdmin ? () => setPerformanceSection('cycles') : undefined}
          />
        )}
        {performanceSection === 'team' && (
          <DashboardTeam onSelectAssignment={handleSelectAssignment} />
        )}
        {performanceSection === 'organization' && (
          <DashboardOrg onSelectAssignment={handleSelectAssignment} />
        )}
        {performanceSection === 'catalog' && (
          <TemplateSelector
            onSelectTemplate={handleSelectTemplate}
            onCreateCustom={handleCreateCustom}
          />
        )}
        {performanceSection === 'cycles' && isAdmin && (
          <CycleManager />
        )}
      </div>

      {/* KPI Detail Panel */}
      <KPIDetailPanel
        assignment={selectedAssignment}
        isOpen={!!selectedAssignmentId}
        onClose={() => setSelectedAssignment(null)}
      />

      {/* Assign KPI Modal */}
      <AssignKPIModal
        isOpen={showAssignModal}
        onClose={() => { setShowAssignModal(false); setSelectedTemplate(null); }}
        template={selectedTemplate}
      />
    </div>
  );
};

export default React.memo(PerformanceView);
